/**
 * 智能体团队主页面
 *
 * 提供智能体团队会话的完整交互界面，使用 AgentTeamPanel 组件。
 */

import React, { useEffect, useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AuthorCenterLayout } from '../components/layout/AuthorCenterLayout';
import { useAuthStore } from '../store/authStore';
import { useAgentTeamStore } from '../store/agentTeamStore';
import { useAgentTeamConfigStore } from '../store/agentTeamConfigStore';
import {
  AgentTeamPanel,
  ConfigModal,
} from '../components/agent-team';

export const AgentTeamPage: React.FC = () => {
  const { id: workspaceIdParam } = useParams<{ id: string }>();
  const workspaceId = parseInt(workspaceIdParam || '0', 10);
  const token = useAuthStore(state => state.token);

  const {
    session,
    loading,
    error,
    initializeWorkspace,
    clearSavedSession,
  } = useAgentTeamStore();

  const { loadConfig } = useAgentTeamConfigStore();

  const [showConfigModal, setShowConfigModal] = useState(false);

  useEffect(() => {
    if (workspaceId > 0 && token) {
      loadConfig(workspaceId);
      initializeWorkspace(workspaceId);
    }
  }, [workspaceId, token, loadConfig, initializeWorkspace]);

  useEffect(() => {
    if (session && session.status === 'completed') {
      clearSavedSession();
    }
  }, [session?.status, clearSavedSession]);

  const handleOpenConfig = useCallback(() => {
    setShowConfigModal(true);
  }, []);

  const handleCloseConfig = useCallback(() => {
    setShowConfigModal(false);
  }, []);

  return (
    <AuthorCenterLayout>
      <div className="flex flex-col h-screen bg-gray-50">
        <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">智能体团队工作台</h1>
            {session && (
              <p className="text-sm text-gray-500 mt-1">
                会话 #{session.id} · {session.title || '新会话'}
              </p>
            )}
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleOpenConfig}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              配置
            </button>
          </div>
        </div>

        {error && (
          <div className="px-6 py-3 bg-red-50 border-b border-red-200">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {loading && !session && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mb-3" />
              <p className="text-sm text-gray-600">正在初始化会话...</p>
            </div>
          </div>
        )}

        {!loading && !session && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <p className="text-gray-600 mb-4">无法创建会话</p>
            </div>
          </div>
        )}

        {workspaceId > 0 && (
          <div className="flex-1 overflow-hidden">
            <AgentTeamPanel workspaceId={workspaceId} />
          </div>
        )}
      </div>

      <ConfigModal
        isOpen={showConfigModal}
        onClose={handleCloseConfig}
        workspaceId={workspaceId}
      />
    </AuthorCenterLayout>
  );
};

export default AgentTeamPage;